import { useSyncExternalStore } from "react";
import { useT } from "../../i18n/hooks.ts";
import {
  aemetJobStore,
  type AemetJob,
  type AemetJobPhase,
} from "../../model/aemet/aemetJobStore.ts";
import { isRestartable } from "../../model/aemet/aemetRangeMath.ts";

/** Never called for `job.phase === "downloading"` — that phase renders its own progress bar instead, see the JSX below. */
function phaseLabel(job: AemetJob, t: ReturnType<typeof useT>): string {
  const map: Record<Exclude<AemetJobPhase, "downloading">, string> = {
    pending: t("aemetJobsPopover.phasePending"),
    "loading-stations":
      job.method === "rocio"
        ? t("aemetJobsPopover.phaseSearchingCells")
        : t("aemetJobsPopover.phaseSearchingStations"),
    "no-stations":
      job.method === "rocio"
        ? t("aemetJobsPopover.phaseNoCoverage")
        : t("aemetJobsPopover.phaseNoStations"),
    "stations-error": t("aemetJobsPopover.phaseError"),
    range: t("aemetJobsPopover.phaseWaitingRange"),
    "download-error": t("aemetJobsPopover.phaseError"),
    report: job.applyErrorMessage
      ? t("aemetJobsPopover.phaseError")
      : t("aemetJobsPopover.phaseApplied"),
  };
  return map[job.phase as Exclude<AemetJobPhase, "downloading">];
}

/**
 * Sort key for AemetJobsPopover.tsx's list — 3 tiers, ascending: actually
 * working (loading stations / awaiting a date range / downloading) first,
 * "pending" (queued, not its turn yet — see `startQueued`) after those,
 * finished (`isRestartable`) last. `Array.prototype.sort` is stable
 * (ES2019+), so within each tier rows keep the order they were started/
 * queued in.
 */
function sortRank(phase: AemetJobPhase): number {
  if (isRestartable(phase)) return 2;
  if (phase === "pending") return 1;
  return 0;
}

/**
 * Every AEMET download job currently tracked, across ALL sub-basins — see
 * aemetJobStore.ts. Independent of which (if any) EditSubBasinDialog.tsx is
 * open: a job started for one sub-basin and left running while the user
 * moved on to edit another one still shows up here, with its own progress
 * and its own cancel button. Only rendered by TopBar.tsx while at least one
 * job exists — dismissing the last one removes the button entirely, same
 * pattern as the "recalculate" button next to "Calculate".
 *
 * Clicking a job's name reopens its small dialog (AemetFetchDialogHost.tsx)
 * — the only way back to it once closed, since EditSubBasinDialog.tsx no
 * longer hosts it (opening it now closes that dialog instead of stacking on
 * top of it). Needed in particular for a job stuck on "report" with a
 * failed apply: its "Retry applying" button lives inside that dialog.
 *
 * Active jobs (still searching stations or downloading, or waiting on a
 * date range) sort to the top, then "pending" ones (queued by
 * `startQueued` but not their turn yet), finished ones (`isRestartable` —
 * applied, errored, no stations) to the bottom — see `sortRank` above. A
 * batch import (the MITECO wizard tab, or "download all") can leave dozens
 * of rows in this list, and the ones actually worth watching shouldn't
 * scroll past the ones that are already done — or get lost among the ones
 * still waiting their turn. "Clear finished" removes every finished row at
 * once instead of dismissing them one by one.
 */
export function AemetJobsPopover() {
  const t = useT();
  const jobs = useSyncExternalStore(aemetJobStore.subscribe, aemetJobStore.getAllJobs);
  const sortedJobs = [...jobs].sort((a, b) => sortRank(a.phase) - sortRank(b.phase));
  const hasFinished = jobs.some((j) => isRestartable(j.phase));

  return (
    <div className="popover aemet-jobs-popover">
      <div className="aemet-jobs-popover-header">
        <h3>{t("aemetJobsPopover.title")}</h3>
        {hasFinished && (
          <button className="button-icon" onClick={() => aemetJobStore.dismissAllFinished()}>
            {t("aemetJobsPopover.clearFinished")}
          </button>
        )}
      </div>
      <ul className="aemet-jobs-list">
        {sortedJobs.map((job) => (
          <li key={job.subBasinCode} className="aemet-job-item">
            <button
              className="aemet-job-name"
              onClick={() => aemetJobStore.openDialog(job.subBasinCode)}
            >
              {job.subBasinName}
            </button>
            <div className="aemet-job-status">
              {job.phase === "downloading" ? (
                (() => {
                  const percent =
                    job.downloadTotal > 0
                      ? Math.round((job.downloadDone / job.downloadTotal) * 100)
                      : 0;
                  return (
                    <div className="aemet-job-progress">
                      <div className="aemet-job-progress-track">
                        <div className="aemet-job-progress-fill" style={{ width: `${percent}%` }} />
                      </div>
                      <span className="aemet-job-progress-count">{percent}%</span>
                    </div>
                  );
                })()
              ) : (
                <span>{phaseLabel(job, t)}</span>
              )}
              <button
                className="button-icon"
                onClick={() => aemetJobStore.dismiss(job.subBasinCode)}
                aria-label={
                  job.phase === "pending" ||
                  job.phase === "loading-stations" ||
                  job.phase === "downloading"
                    ? t("aemetJobsPopover.cancel")
                    : t("aemetJobsPopover.remove")
                }
                title={
                  job.phase === "pending" ||
                  job.phase === "loading-stations" ||
                  job.phase === "downloading"
                    ? t("aemetJobsPopover.cancel")
                    : t("aemetJobsPopover.remove")
                }
              >
                ✕
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
